import React, { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import {
  MyAccountMenuItemsPages,
  MyAccountMenuItemsModel,
} from "../../../my-account/MyAccountMenuItems";
import { UploadImageStateModel } from "../../../../components/image-upload/ImageUploadModel";
import PageLayout from "../../../_layout/PageLayout";
import Form from "../../../../components/form/Form";
import { FormikHelpers, FormikValues } from "formik";
import { UserProfileEditInput } from "../../../../api-models/request/UserInput";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { ChangePassword } from "../../../../components/data-components/ChangePassword";
import {
  DataSection,
  SubSection,
} from "../../../../components/data-components/sections/DataSections";
import ImageUpload from "../../../../components/image-upload/ImageUpload";
import { EditFirstName, EditLastName, EditUserName } from "../../components/DataComponents";
import DisplayCreatedBy from "../../../../components/data-components/DisplayCreatedBy";
import DisplayDateCreated from "../../../../components/data-components/DisplayDateCreated";
import EditEmail from "../../../../components/data-components/EditEmail";
import EditPhone from "../../../../components/data-components/EditPhone";
import { HeaderProfile } from "../../../../components/svg/svgImageList";
import { CurrentUserProfileStorageModel } from "../../../../services/user/CurrentUserProfileStorage";
import { api } from "../../../../services/api/ApiService";
import { warningLeavePageService } from "../../../../services/page/WarningLeavePageService";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [imageUploadState] = useState(() => UploadImageStateModel.create({}));

  const [currentUserProfileStorage] = useState(() =>
    CurrentUserProfileStorageModel.create({}, { api })
  );

  const [myAccountMenuItems] = useState(() =>
    MyAccountMenuItemsModel.create({ menuItemPage: MyAccountMenuItemsPages.EditAccountDetails })
  );

  const goToMyAccountPage = useCallback(() => history.push("/my-account"), [history]);

  const getInitialValues = useCallback(
    () => ({
      firstName: currentUserProfileStorage.currentUser.firstName || "",
      lastName: currentUserProfileStorage.currentUser.lastName || "",
      userName: currentUserProfileStorage.currentUser.userName,
      email: currentUserProfileStorage.currentUser.email,
      phone: currentUserProfileStorage.currentUser.phone,
    }),
    [currentUserProfileStorage.currentUser]
  );

  const menuItems = {
    staticItems: myAccountMenuItems.getStaticItems(),
  };

  const updateUserImage = useCallback(async () => {
    if (imageUploadState.shouldDeleteCurrentImage)
      return currentUserProfileStorage.deleteMyUserProfileImage();
    if (imageUploadState.imageForUpload)
      return currentUserProfileStorage.editMyUserProfileImage(imageUploadState.imageForUpload);
  }, [
    currentUserProfileStorage,
    imageUploadState.imageForUpload,
    imageUploadState.shouldDeleteCurrentImage,
  ]);

  const updateUserProfileDetails = useCallback(
    async (values: FormikValues) => {
      const user: UserProfileEditInput = {
        firstName: values.firstName,
        userName: values.userName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone || null,
      };
      await currentUserProfileStorage.editMyUserProfileDetails(user);
    },
    [currentUserProfileStorage]
  );

  const changePassword = useMemo(() => {
    return <ChangePassword />;
  }, []);

  const handleSubmit = async (values: FormikValues, formActions: FormikHelpers<FormikValues>) => {
    try {
      if (imageUploadState.shouldUpdateImage()) await updateUserImage();
      await updateUserProfileDetails(values);
      warningLeavePageService.setIsDirty(false);
      return goToMyAccountPage();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(values, formActions);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  };

  return (
    <PageLayout
      titleKey="EditMyAccountDetails.title"
      titleContext={{ userName: currentUserProfileStorage.currentUser.userName }}
      id="view-user-details"
      menuItems={menuItems}
      isLoading={currentUserProfileStorage.isLoading}
    >
      <Form
        formikOptions={{
          initialValues: getInitialValues(),
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
        }}
        onCancel={goToMyAccountPage}
        rightSideActionButton={changePassword}
      >
        {({ values }) => (
          <>
            <DataSection labelKey="EditMyAccountDetails.details">
              <SubSection className="image">
                <ImageUpload
                  imageUploadState={imageUploadState}
                  initialImageUri={currentUserProfileStorage.currentUser?.image?.url}
                  placeholderImage={HeaderProfile}
                  className="my-account"
                />
              </SubSection>

              <SubSection>
                <EditFirstName firstName={values.firstName} />
                <EditLastName lastName={values.lastName} />
                <EditUserName userName={values.userName} />
              </SubSection>

              <SubSection>
                <EditEmail email={values.email} />
                <EditPhone phone={values.phone} />
              </SubSection>

              <SubSection>
                <DisplayDateCreated date={currentUserProfileStorage.currentUser.registrationDate} />
                <DisplayCreatedBy createdBy={currentUserProfileStorage.currentUser.userName} />
              </SubSection>
            </DataSection>
          </>
        )}
      </Form>
    </PageLayout>
  );
});
